/**
 * n8n is only used for Google Sheets (ADR 021).
 *
 * Worker → n8n: POST `${N8N_SHEETS_WEBHOOK_URL}` with `SheetSyncRequest`, signed:
 *   X-LSC-Timestamp: <unix seconds>
 *   X-LSC-Signature: v1=<hex(HMAC-SHA256(N8N_WEBHOOK_SIGNING_SECRET, `${timestamp}.${rawBody}`))>
 * n8n answers 2xx with `SheetSyncAck` immediately and reports the result asynchronously via
 * POST /api/v1/internal/n8n/sheet-result (`SheetSyncResult`, signed the same way).
 * The sheet is export/reporting only – it never writes back into D1 (§54).
 *
 * Parts (CR-WS1-2, ADR 067): a batch is exported in `partCount` requests of at most
 * SHEET_SYNC_ROWS_PER_PART rows each (10 ms CPU per queue invocation). The worker sends part n+1
 * only after the SUCCEEDED callback of part n, so part n+1 always carries `existingSheetId`.
 *   part 1   → create the sheet if `existingSheetId` is null, clear A:C, write header + rows at A1
 *   part > 1 → write the rows into `existingSheetId` at row `rowOffset + 2` (header = row 1);
 *              position-based (not "append"), so a retried/duplicated part is idempotent.
 */
import { z } from 'zod';
import { ActivationCodeStatusSchema } from '../enums';

export const N8N_CONTRACT_VERSION = 1;

export const SheetSyncRowSchema = z
  .object({
    /** `XXXXX-XXXXX` */
    code: z.string(),
    status: ActivationCodeStatusSchema,
    /** German label for the customer sheet, e.g. „Aktiviert“. */
    statusLabel: z.string(),
    /** ISO 8601; set when REDEEMED. */
    activatedAt: z.iso.datetime().nullable(),
  })
  .strict();
export type SheetSyncRow = z.infer<typeof SheetSyncRowSchema>;

export const SheetSyncRequestSchema = z
  .object({
    version: z.literal(N8N_CONTRACT_VERSION),
    jobId: z.uuid(),
    idempotencyKey: z.string(),
    batch: z
      .object({
        id: z.uuid(),
        name: z.string(),
        companyName: z.string(),
        /** Existing sheet to update; null → n8n creates a new sheet. */
        existingSheetId: z.string().nullable(),
      })
      .strict(),
    /** 1-based part of this sync (ADR 067). */
    part: z.number().int().positive(),
    /** Total parts of this sync; fixed by part 1. */
    partCount: z.number().int().positive(),
    /** 0-based index of `rows[0]` within the whole export (= (part − 1) × rows per part). */
    rowOffset: z.number().int().nonnegative(),
    /** Only this part's rows, sorted by code creation; no private emails by default (§54). */
    rows: z.array(SheetSyncRowSchema),
    generatedAt: z.iso.datetime(),
    /** Absolute URL of /api/v1/internal/n8n/sheet-result. */
    callbackUrl: z.url(),
  })
  .strict()
  .refine((request) => request.part <= request.partCount, {
    message: 'part must not exceed partCount',
    path: ['part'],
  })
  .refine((request) => request.part === 1 || request.batch.existingSheetId !== null, {
    message: 'parts > 1 require existingSheetId',
    path: ['batch', 'existingSheetId'],
  });
export type SheetSyncRequest = z.infer<typeof SheetSyncRequestSchema>;

/** Synchronous acknowledgement by the n8n webhook. */
export const SheetSyncAckSchema = z.looseObject({ accepted: z.literal(true) });
export type SheetSyncAck = z.infer<typeof SheetSyncAckSchema>;

export const SheetSyncResultSchema = z
  .object({
    version: z.literal(N8N_CONTRACT_VERSION),
    jobId: z.uuid(),
    batchId: z.uuid(),
    outcome: z.enum(['SUCCEEDED', 'FAILED']),
    /** Echo of `SheetSyncRequest.part` / `partCount` (ADR 067). */
    part: z.number().int().positive(),
    partCount: z.number().int().positive(),
    sheetId: z.string().min(1).nullable(),
    sheetUrl: z.url().nullable(),
    /** Rows written by this part. */
    rowCount: z.number().int().nonnegative().nullable(),
    /** Sanitised error text when FAILED. */
    error: z.string().max(1000).nullable(),
    completedAt: z.iso.datetime(),
  })
  .strict();
export type SheetSyncResult = z.infer<typeof SheetSyncResultSchema>;
